/**
 * WashPros - Theme Provider
 * Dark/Light theme toggle with accent color customization
 * Persistent user preferences via localStorage
 * NOTE: Only stores UI preferences (theme, accent color) — NOT sensitive data.
 * Safe for localStorage per OWASP guidelines (no auth tokens, PII, or secrets).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

// Available accent color presets
export const ACCENT_COLORS = {
  cyan: {
    name: "Cyan (Default)",
    primary: "#22d3ee",
    secondary: "#06b6d4",
    hover: "#0891b2",
    light: "rgba(34, 211, 238, 0.1)",
    gradient: "from-cyan-500 to-cyan-400"
  },
  blue: {
    name: "Ocean Blue",
    primary: "#3b82f6",
    secondary: "#2563eb",
    hover: "#1d4ed8",
    light: "rgba(59, 130, 246, 0.1)",
    gradient: "from-blue-500 to-blue-400"
  },
  purple: {
    name: "Royal Purple",
    primary: "#a855f7",
    secondary: "#9333ea",
    hover: "#7c3aed",
    light: "rgba(168, 85, 247, 0.1)",
    gradient: "from-purple-500 to-purple-400"
  },
  emerald: {
    name: "Emerald Green",
    primary: "#10b981",
    secondary: "#059669",
    hover: "#047857",
    light: "rgba(16, 185, 129, 0.1)",
    gradient: "from-emerald-500 to-emerald-400"
  },
  rose: {
    name: "Rose Pink",
    primary: "#f43f5e",
    secondary: "#e11d48",
    hover: "#be123c",
    light: "rgba(244, 63, 94, 0.1)",
    gradient: "from-rose-500 to-rose-400"
  },
  amber: {
    name: "Golden Amber",
    primary: "#f59e0b",
    secondary: "#d97706",
    hover: "#b45309",
    light: "rgba(245, 158, 11, 0.1)",
    gradient: "from-amber-500 to-amber-400"
  }
};

const ThemeContext = createContext({
  theme: "dark",
  accentColor: "cyan",
  toggleTheme: () => {},
  setTheme: () => {},
  setAccentColor: () => {},
  isDark: true,
  accent: ACCENT_COLORS.cyan
});

export const useTheme = () => useContext(ThemeContext);

const THEME_STORAGE_KEY = "washpros-theme";
const ACCENT_STORAGE_KEY = "washpros-accent";

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    // Get from localStorage or default to dark
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === "light" || stored === "dark") {
        return stored;
      }
      // Check system preference
      if (window.matchMedia("(prefers-color-scheme: light)").matches) {
        return "light";
      }
    }
    return "dark";
  });

  const [accentColor, setAccentColorState] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(ACCENT_STORAGE_KEY);
      if (stored && ACCENT_COLORS[stored]) {
        return stored;
      }
    }
    return "cyan";
  });

  // Apply theme and accent to document
  useEffect(() => {
    const root = document.documentElement;
    const accent = ACCENT_COLORS[accentColor] || ACCENT_COLORS.cyan;
    
    // Set accent color CSS variables
    root.style.setProperty("--accent-primary", accent.primary);
    root.style.setProperty("--accent-secondary", accent.secondary);
    root.style.setProperty("--accent-hover", accent.hover);
    root.style.setProperty("--accent-light", accent.light);
    
    if (theme === "light") {
      // Phase 7g.15.23: apply BOTH `light` and `light-mode` classes so the
      // App.css safety layer (~hundreds of `.light-mode` selectors covering
      // backgrounds, text contrast, borders, cards, inputs, buttons, modals)
      // activates. Previously only `light` was applied, leaving most of the
      // light-mode styling dormant.
      root.classList.remove("dark");
      root.classList.add("light", "light-mode");
      document.body.classList.remove("dark");
      document.body.classList.add("light", "light-mode");
      root.style.setProperty("--bg-primary", "#f8fafc");
      root.style.setProperty("--bg-secondary", "#ffffff");
      root.style.setProperty("--bg-tertiary", "#f1f5f9");
      root.style.setProperty("--text-primary", "#0f172a");
      root.style.setProperty("--text-secondary", "#475569");
      root.style.setProperty("--text-muted", "#94a3b8");
      root.style.setProperty("--border-color", "#e2e8f0");
      root.style.setProperty("--card-bg", "#ffffff");
      root.style.setProperty("--card-border", "#e2e8f0");
    } else {
      root.classList.remove("light", "light-mode");
      root.classList.add("dark");
      document.body.classList.remove("light", "light-mode");
      document.body.classList.add("dark");
      root.style.setProperty("--bg-primary", "#0f172a");
      root.style.setProperty("--bg-secondary", "#1e293b");
      root.style.setProperty("--bg-tertiary", "#334155");
      root.style.setProperty("--text-primary", "#f8fafc");
      root.style.setProperty("--text-secondary", "#cbd5e1");
      root.style.setProperty("--text-muted", "#64748b");
      root.style.setProperty("--border-color", "rgba(255,255,255,0.1)");
      root.style.setProperty("--card-bg", "rgba(30,41,59,0.5)");
      root.style.setProperty("--card-border", "rgba(255,255,255,0.1)");
    }
    
    // Save to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    localStorage.setItem(ACCENT_STORAGE_KEY, accentColor);
  }, [theme, accentColor]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (!stored) {
        setThemeState(e.matches ? "light" : "dark");
      }
    };
    
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => prev === "dark" ? "light" : "dark");
  }, []);

  const setTheme = useCallback((newTheme) => {
    if (newTheme === "light" || newTheme === "dark") {
      setThemeState(newTheme);
    }
  }, []);

  const setAccentColor = useCallback((color) => {
    if (ACCENT_COLORS[color]) {
      setAccentColorState(color);
    }
  }, []);

  const themeValue = React.useMemo(() => ({
    theme,
    accentColor,
    toggleTheme,
    setTheme,
    setAccentColor,
    isDark: theme === "dark",
    accent: ACCENT_COLORS[accentColor] || ACCENT_COLORS.cyan
  }), [theme, accentColor, toggleTheme, setTheme, setAccentColor]);

  return (
    <ThemeContext.Provider value={themeValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
